import moment from 'moment'

/**
 * Khoảng thời gian dùng chung cho các màn của module — một định nghĩa, hai màn dùng.
 *
 * Trước đây M17 tự khai preset trong `WorkloadPage`, còn M18 dùng hai ô chọn ngày rời không có
 * preset nào. Hai màn hỏi cùng một câu ("lấy dữ liệu trong khoảng nào") mà thao tác khác hẳn nhau,
 * nên người dùng phải học lại từ đầu khi chuyển màn.
 */

/**
 * Các khoảng TRƯỢT, tính bằng số ngày tính đến hết hôm nay.
 *
 * Tính theo NGÀY LÀM VIỆC trọn vẹn, không phải "N×24 giờ qua" — người xưởng nói "xem 7 ngày" là 7
 * ngày lịch, và mọi con số sản lượng đều chốt theo ngày.
 */
export const DATE_PRESETS = [7, 30]

/**
 * Khoảng trượt của hai màn BÁO CÁO M18 — chỉ còn `7 ngày`.
 *
 * Bỏ `30 ngày` khi màn báo cáo có preset theo THÁNG: hai thứ gần như trùng nhau nhưng không bằng
 * nhau ("30 ngày" kết thúc hôm nay và vắt qua hai tháng), nên đặt cạnh nhau là mời người dùng bấm
 * nhầm rồi đối chiếu với bảng công theo tháng và thấy lệch mà không hiểu vì sao.
 *
 * ⚠️ M17 (`WorkloadPage`) GIỮ `30 ngày` — nó không có preset tháng và cũng không được có: màn đó
 *    lọc theo `created` của kiện, gom theo UTC, không có luật ngày trọn, nên "tháng" ở đó không
 *    khớp tháng của bảng công. Hai danh sách khác nhau là CỐ Ý, không phải quên đồng bộ.
 */
export const REPORT_DATE_PRESETS = [7]

/**
 * Chuẩn hoá GIÂY của hai đầu một khoảng do người dùng chọn.
 *
 * Mọi ô chọn thời gian của module hiện `HH:mm` — không có giây. Nhưng giá trị gửi lên server là một
 * mốc ISO đầy đủ, và antd đặt giây = 0 cho CẢ HAI đầu khi chọn từ bảng lịch.
 *
 * Hệ quả nếu để nguyên: chọn "đến 23:59" thật ra là `23:59:00.000`, và **59 giây cuối của phút đó
 * bị loại khỏi báo cáo**. Một sản phẩm quét lúc 23:59:30 sẽ không có trong báo cáo hôm nay, cũng
 * không có ở hôm sau (hôm sau bắt đầu từ 00:00) — biến mất khỏi mọi con số mà không có dấu hiệu nào.
 *
 * Quy ước: đầu `từ` lấy ĐẦU phút, đầu `đến` lấy CUỐI phút. Chọn 08:00 → 23:59 nghĩa là trọn vẹn
 * khoảng đó, đúng như cách người dùng nói bằng lời.
 */
export const rangeStartOf = (value) => (value ? value.clone().second(0).millisecond(0) : value)

export const rangeEndOf = (value) => (value ? value.clone().second(59).millisecond(999) : value)

/**
 * Khoảng gửi lên cho BÁO CÁO M18 — biên NGÀY VIỆT NAM, suy từ ngày người dùng NHÌN THẤY.
 *
 * ⚠️ `moment().startOf('day').toISOString()` là biên ngày của **múi giờ trình duyệt**, không phải
 *    của ngày VN. Service chỉ tính những ngày VN nằm TRỌN trong khoảng (BR-2.1), nên gửi biên
 *    trình duyệt là tự cắt mất ngày — đo thật: máy UTC+0 hoặc UTC+8 chọn "Hôm nay" ra **không còn
 *    ngày trọn nào**, tức cả 6 cột hiệu suất trống. Máy ở xưởng đặt UTC+7 thì không dính, nhưng
 *    "không dính vì máy đang đặt đúng" không phải một bảo đảm.
 *
 *    Lấy `format('YYYY-MM-DD')` rồi dán offset `+07:00` chứ không `.utcOffset(7)`: ta muốn ĐÚNG
 *    ngày người dùng vừa bấm trên lịch, không muốn quy đổi mốc của họ sang giờ VN rồi lấy ngày của
 *    kết quả — hai cách đó khác nhau đúng ở nhóm người dùng đang hỏng.
 *
 * KHÔNG dùng cho M17: màn đó lọc theo `created` của barcode, gom theo UTC, và không có luật ngày trọn.
 */
export const vnDayRangeIso = (from, to) => ({
    from: `${from.format('YYYY-MM-DD')}T00:00:00.000+07:00`,
    to: `${to.format('YYYY-MM-DD')}T23:59:59.999+07:00`,
})

/** Hôm nay, trọn ngày — mặc định của mọi màn có khoảng thời gian. */
export const todayRange = () => [moment().startOf('day'), moment().endOf('day')]

/** Bảy ngày tính đến hết hôm nay — mặc định khi đo tỷ lệ làm lại từ sản lượng thực tế. */
export const lastWeekRange = () => [moment().startOf('day').subtract(6, 'days'), moment().endOf('day')]

/**
 * Bảng preset cho `ranges` của `DatePicker.RangePicker`.
 *
 * Trả về HÀM cho từng khoảng, không phải giá trị dựng sẵn: người dùng mở màn hình lúc 23:58 rồi bấm
 * preset lúc 00:02 hôm sau phải ra khoảng của ngày MỚI. Dựng sẵn lúc render thì mốc đóng băng ở thời
 * điểm render.
 */
export const presetRanges = (slidingDays = DATE_PRESETS) => {
    // Hai loại khoảng khác nhau về bản chất, nên không gộp vào một vòng lặp:
    //   · MỘT NGÀY CỐ ĐỊNH (hôm nay, hôm qua) — dùng để chốt sản lượng của đúng ngày đó;
    //   · KHOẢNG TRƯỢT (7/30 ngày) — dùng để nhìn xu hướng.
    // "Hôm qua" KHÔNG phải "1 ngày trước đến giờ": nó kết thúc lúc 23:59 hôm qua, không kéo sang
    // hôm nay. Gộp chung công thức trượt sẽ cho ra một khoảng vắt qua hai ngày, và con số hiện ra
    // không khớp bất kỳ báo cáo chốt ngày nào.
    const ranges = {
        'Hôm nay': () => [moment().startOf('day'), moment().endOf('day')],
        'Hôm qua': () => [
            moment().subtract(1, 'day').startOf('day'),
            moment().subtract(1, 'day').endOf('day'),
        ],
    }

    slidingDays.forEach((days) => {
        ranges[`${days} ngày`] = () => [
            moment().startOf('day').subtract(days - 1, 'days'),
            moment().endOf('day'),
        ]
    })

    return ranges
}

/**
 * Preset cho hai màn BÁO CÁO M18 — khoảng trượt CỘNG tháng lịch.
 *
 * Người xưởng hỏi báo cáo theo hai kiểu khác nhau và cần cả hai: "mấy ngày gần đây thế nào" (khoảng
 * trượt) và "tháng 9 ra sao" (tháng lịch, để đối chiếu với bảng công và với báo cáo vận hành đã chốt
 * theo tháng). Trước đây màn báo cáo chỉ có kiểu thứ nhất, nên muốn xem một tháng là phải tự kéo
 * hai đầu lịch — thao tác dễ lệch một ngày, mà lệch một ngày ở đây là lệch cả tháng công.
 *
 * Khoảng trượt ở đây rút còn `7 ngày` — xem `REPORT_DATE_PRESETS`.
 *
 * ⚠️ Gộp ở ĐÂY chứ không gộp tại chỗ gọi: hai màn báo cáo dùng chung một component, nhưng màn M17
 *    (`WorkloadPage`) thì KHÔNG được thêm tháng — nó lọc theo `created` của kiện, gom theo UTC và
 *    không có luật ngày trọn, nên một "tháng" ở đó không khớp tháng của bảng công. Hai hàm riêng
 *    giữ đúng ranh giới ấy.
 */
export const reportPresetRanges = () => ({...presetRanges(REPORT_DATE_PRESETS), ...recentMonthRanges()})

/** Bao nhiêu tháng gần nhất xuất hiện trong preset — gồm cả tháng ĐANG chạy. */
export const RECENT_MONTH_COUNT = 4

/**
 * Preset theo THÁNG LỊCH, cho các màn làm việc theo kỳ (nhập chấm công, đối chiếu công).
 *
 * Khác hẳn khoảng trượt `7 ngày`/`30 ngày` ở trên và vì thế không gộp vào đó: "30 ngày" kết thúc
 * hôm nay và vắt qua hai tháng, còn giờ công thì HR chốt theo tháng lịch. Người đi nhập cầm file
 * `CCT9.xlsx` trên tay, họ cần đúng 01/09–30/09 chứ không cần một cửa sổ trượt.
 *
 * ⚠️ Tháng ĐANG chạy kết thúc ở HÔM NAY, không ở ngày cuối tháng. `ranges` của antd đi vòng qua
 *    `disabledDate`, nên trả 31/10 khi mới 28/10 là tự tay đặt vào một mốc mà chính ô chọn ngày
 *    không cho chọn — và lưới vẽ thêm ba cột tương lai không bấm được.
 *
 * ⚠️ Nhãn chỉ ghi năm khi năm KHÁC năm hiện tại. Tháng 1 nhìn ngược lại bốn tháng là chạm tháng 12
 *    năm ngoái, và hai nhãn "Tháng 12" · "Tháng 10" cạnh nhau thì không ai đoán ra cái nào năm nào.
 *
 * Trả HÀM cho từng khoảng, cùng lý do với `presetRanges` — xem docblock ở đó.
 */
export const recentMonthRanges = (count = RECENT_MONTH_COUNT) => {
    const ranges = {}

    for (let back = 0; back < count; back += 1) {
        const anchor = moment().subtract(back, 'months')
        const label = anchor.year() === moment().year()
            ? `Tháng ${anchor.month() + 1}`
            : `Tháng ${anchor.month() + 1}/${anchor.year()}`

        // `back` đóng băng trong closure; mốc thì tính lại lúc bấm, nên qua nửa đêm vẫn đúng.
        ranges[label] = () => {
            const at = moment().subtract(back, 'months')
            const end = at.clone().endOf('month')

            return [at.clone().startOf('month'), moment.min(end, moment().endOf('day'))]
        }
    }

    return ranges
}
